// The formula behind a route's figure (TX-US-0032), as LaTeX for KaTeX to typeset: every input
// over the brace that names it, worked out to the figure. No figure is calculated here - each one
// printed is a field of the route or a value the API sent - so a figure rounded up is shown
// between ceiling brackets, not with the fraction it was rounded from.

import { braced, number, result, text } from '../formulaTex.js';
import { DAY_FIELDS } from './routeFields.js';

// As a route stores them: four decimal places at most.
const PLACES = 4;

const days = (value) => (
  `${number(value, PLACES)}\\,${text(Number(value) === 1 ? 'day' : 'days')}`
);

const UNIT_TEX = { M3: `${text('m')}^3`, MT: text('MT'), L: text('L') };
const quantity = (value, unit) => `${number(value, PLACES)}\\,${UNIT_TEX[unit]}`;

const over = (top, bottom) => `\\dfrac{${top}}{${bottom}}`;
const roundedUp = (tex) => `\\left\\lceil ${tex} \\right\\rceil`;

// "Loading / queue (days)" names the leg and its unit; the brace names the leg alone.
const legName = (label) => label.replace(/ \(days\)$/, '');

const month = (daysPerMonth) => braced(days(daysPerMonth), 'Days in a month');
const cycle = (requirement) => braced(days(requirement.cycle_days), 'Total cycle');
const trips = (requirement) => braced(number(requirement.trips_required, 0), 'Trips required');

export const cycleTex = (route, requirement) => {
  const legs = DAY_FIELDS.map(({ key, label }) => braced(days(route[key]), legName(label)));
  return `${legs.join(' + ')} = ${result(days(requirement.cycle_days))}`;
};

export const tripsPerTruckTex = (requirement, daysPerMonth) => (
  `${over(month(daysPerMonth), cycle(requirement))} = ${result(number(requirement.trips_per_truck, 2))}`
);

export const tripsRequiredTex = (route, requirement) => {
  const target = braced(quantity(route.monthly_target_m3, route.unit), 'Monthly target');
  const capacity = braced(quantity(route.truck_capacity_m3, route.unit), 'Truck capacity');
  return `${roundedUp(over(target, capacity))} = ${result(number(requirement.trips_required, 0))}`;
};

export const trucksRequiredTex = (requirement, daysPerMonth) => {
  const work = `${trips(requirement)} \\times ${cycle(requirement)}`;
  return `${roundedUp(over(work, month(daysPerMonth)))} = ${result(number(requirement.trucks_required, 0))}`;
};

export const loadsPerDayTex = (requirement, daysPerMonth) => (
  `${over(trips(requirement), month(daysPerMonth))} = ${result(number(requirement.loads_per_day, 2))}`
);
